/**
 * @description 图表助手、数据转换、配置
 * @author zhijie.huang
 * @date 2018/11/11
 */

import { extend, getKeyByValue } from '@/utils/tool'
import TimeFormat from '@/utils/timeFormat'

const DATA_FORMAT = {
  'PERCENT': 'PERCENT',
  'EXTRAPERCENT': 'EXTRAPERCENT',
  'NUMBER': 'NUMBER',
  'SIZE': 'SIZE',
  'TIME': 'TIME',
  'TIMELONG': 'TIMELONG'
}

const ITEM_COLOR = [
  '#FF0000',
  '#FF8700',
  '#1AAD19',
  '#0B8DE5',
  '#1BCFB3',
  '#6952DF',
  '#DEA409',
  '#9F3BC9',
  '#E34091'
]

const TYPE_MAP = {
  'BAR': 'BAR',
  'AREA': 'AREA',
  'LINE': 'LINE',
  'PIE': 'PIE'
}

class ChartHelper {
  static BASE_OPTION = function () {
    return {
      backgroundColor: '#FFF',
      color: ITEM_COLOR,
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        data: []
      },
      xAxis: {
        axisLine: {
          lineStyle: {
            color: '#d8d8d8'
          }
        },
        // 轴线与标签对齐
        axisTick: {
          alignWithLabel: true
        },
        axisLabel: {
          color: '#7c7c7c'
        },
        boundaryGap: false
      },
      yAxis: {
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        max: null,
        // splitNumber: 5,
        axisLabel: {
          color: '#7c7c7c'
        },
        splitLine: {
          lineStyle: {
            color: '#d8d8d8'
          }
        }
      }
    }
  }

  static PIE_BASE_OPTION = function () {
    return {
      backgroundColor: '#FFF',
      tooltip: {
        trigger: 'item'
      },
      legend: {
        data: []
      }
    }
  }

  static SERIES_LINE = function ({color}) {
    return {
      type: 'line',
      symbol: 'none',
      smooth: true,
      itemStyle: {
        normal: {
          color: color
        }
      },
      lineStyle: {
        normal: {
          width: 1
        }
      }
    }
  }

  static SERIES_AREA = function ({color}) {
    return {
      type: 'line',
      symbol: 'none',
      smooth: true,
      itemStyle: {
        normal: {
          color: color
        }
      },
      lineStyle: {
        normal: {
          width: 1
        }
      },
      areaStyle: {
        normal: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: color
            }, {
              offset: 1, color: '#FFFFFF'
            }]
          }
        }
      }
    }
  }

  static SERIES_BAR = function ({color}) {
    return {
      type: 'bar',
      itemStyle: {
        normal: {
          color: color
        }
      }
    }
  }

  static SERIES_PIE = function () {
    return {
      type: 'pie',
      radius: ['50%', '70%'],
      avoidLabelOverlap: false,
      label: {
        normal: {
          show: false,
          position: 'center'
        },
        emphasis: {
          show: true,
          textStyle: {
            fontSize: '30',
            fontWeight: 'bold'
          }
        }
      },
      labelLine: {
        normal: {
          show: false
        }
      }
    }
  }

  static DOT_COLOR = function (color) {
    return `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${color}"></span>`
  }

  static getUnit (unit, data) {
    const SIZE_DATA_PARSE = 1024
    const unitArr = ['BYTE', 'KB', 'MB', 'GB']
    const newUnit = unitArr.indexOf(unit) !== -1 ? unit : unitArr[0]

    if (newUnit === 'GB') {
      return `${data}${newUnit}`
    } else if (data > SIZE_DATA_PARSE) {
      const index = unitArr.indexOf(newUnit) + 1
      data = parseInt(data / SIZE_DATA_PARSE)
      return ChartHelper.getUnit(unitArr[index], data)
    } else {
      return `${data}${newUnit}`
    }
  }

  // 格式化方法
  static formatType (type = '') {
    const typeHandler = {}
    let unit = ''
    // NUMBER-ms
    if (type.match(/NUMBER/)) {
      if (type.match(/NUMBER-(\w+)/)) unit = type.match(/NUMBER-(\w+)/)[1]
      type = DATA_FORMAT.NUMBER
    }
    typeHandler[DATA_FORMAT.NUMBER] = function (val) {
      return unit ? `${val}${unit}` : val
    }
    typeHandler[DATA_FORMAT.TIME] = function (val) {
      if ((val + '').length < 13) val = val * 1000
      const time = TimeFormat.getFormatTime(val)
      return time.replace(/\d{4}-(\d{2}-\d{2})\W(\d{2}:\d{2}):\d{2}/, '$2\n$1')
    }
    typeHandler[DATA_FORMAT.TIMELONG] = function (val) {
      if ((val + '').length < 13) val = val * 1000
      const time = TimeFormat.getFormatTime(val)
      return time.split(' ')[0] + '\n' + time.split(' ')[1]
    }
    // SIZE-BYTE
    if (type.match(/SIZE/)) {
      type = type.toUpperCase()
      type = DATA_FORMAT.SIZE
      if (type.match(/SIZE-(\w+)/)) unit = type.match(/SIZE-(\w+)/)[1]
    }
    typeHandler[DATA_FORMAT.SIZE] = function (val) {
      let _unit = unit
      return ChartHelper.getUnit(_unit, val)
    }
    typeHandler[DATA_FORMAT.PERCENT] = function (val) {
      return `${val}%`
    }
    typeHandler[DATA_FORMAT.EXTRAPERCENT] = function (val) {
      return `${(val * 100).toFixed(0)}%`
    }

    return typeHandler[type] ? typeHandler[type] : typeHandler[DATA_FORMAT.NUMBER]
  }

  /**
   * 数据处理
   * 暂时支持：（折线图、面积图、柱状图、饼图）
   * 有数据返回 echarts 配置项/没数据返回 false
   * @param {*} data 数据
   * @param {*} chartIndex 索引
   * @param {*} type 图表类型
   * @param {*} chartOption 其他echarts配置项
   */
  static chartDataHandle (data, chartIndex, type, chartOption) {
    if (type === TYPE_MAP.PIE) {
      return ChartHelper.chartDataHandlePie(data, chartIndex, chartOption)
    } else if (TYPE_MAP[type]) {
      return ChartHelper.chartDataHandleBase(data, chartIndex, type, chartOption)
    } else {
      throw new Error(`chartHelper error, not support chart type = ${type}`)
    }
  }

  // 数据处理（折线图、面积图、柱状图）
  static chartDataHandleBase (data, chartIndex, type, chartOption) {
    const getDefaultSeriesOptions = ChartHelper[`SERIES_${type}`]
    const rows = data.rows
    const target = data.columns[0]
    const columns = data.columns.slice(1)
    const formatMap = data.formatMap
    const seriesStyle = data.seriesStyle
    const colors = data.colors
    const labelMap = data.labelMap
    const xName = data.xName

    if (rows && rows.length === 0) {
      return false
    }

    let colorList = colors || ITEM_COLOR
    const colorIndex = (chartIndex || 0) % colorList.length
    let color = colorList[colorIndex]

    let options = extend(true, {}, chartOption)
    const dataX = []
    const dataYList = {}
    const series = []
    let xFormatFun = ChartHelper.formatType()
    let yFormatFun = {}

    // x轴y轴数据
    rows.map((item, index) => {
      if (item[target] !== undefined) {
        dataX.push(item[target])
      }
      columns.map((y) => {
        if (!dataYList[y]) {
          dataYList[y] = []
        }
        if (item[y] !== undefined) {
          dataYList[y].push(item[y])
        }
      })
    })

    // 设置 series
    columns.map((y, index) => {
      // 如果多条线，另外配颜色
      if (columns.length > 1) color = colorList[index]
      const yName = (labelMap && labelMap[y]) ? labelMap[y] : ''
      let seriesOption = extend(true, {}, getDefaultSeriesOptions({color}))
      if (seriesStyle) seriesOption = extend(true, { ...seriesOption }, seriesStyle)
      let item = extend(true, {
        data: dataYList[y],
        name: yName
      }, seriesOption)
      series.push(item)

      // 设置 y轴 格式化
      if (formatMap && formatMap[y]) {
        // y轴只能一种format？
        yFormatFun[y] = ChartHelper.formatType(formatMap[y])
        options.yAxis.axisLabel.formatter = ChartHelper.formatType(formatMap[y])
      } else {
        options.yAxis.axisLabel.formatter = ChartHelper.formatType()
      }
    })

    // 设置 x轴 名称
    if (xName) {
      options.xAxis.name = xName
      options.xAxis.nameLocation = 'center'
      options.xAxis.nameGap = 38
      options.xAxis.nameTextStyle = {
        color: '#7c7c7c'
      }
    }

    // 设置 x轴 格式化
    if (formatMap && formatMap[target]) {
      xFormatFun = ChartHelper.formatType(formatMap[target])
      options.xAxis.axisLabel.formatter = xFormatFun
    }

    // tooltip格式化
    options.tooltip.formatter = function (params, ticket, callback) {
      let text = ''
      let x = ''
      let y = ''
      params.map((item) => {
        x = xFormatFun ? xFormatFun(item.name) : item.name
        let aYFormatFun = null
        if (labelMap && item.seriesName) {
          aYFormatFun = yFormatFun[getKeyByValue(labelMap, item.seriesName)]
        }
        if (item.color) {
          y += ChartHelper.DOT_COLOR(item.color)
        }
        if (aYFormatFun) {
          y += `${item.seriesName}: ${aYFormatFun(item.value)}</br>`
        } else {
          y += `${item.seriesName}: ${item.value}</br>`
        }
      })
      text = `${x} <br> ${y}`
      return text
    }

    options.xAxis.data = dataX
    options.series = series
    // if (legend && legend.length > 1) {
    //   options.legend.data = legend
    // }

    return options
  }

  // 数据处理（饼图）
  static chartDataHandlePie (data, chartIndex, chartOption) {
    const getDefaultSeriesOptions = ChartHelper['SERIES_PIE']
    const rows = data.rows
    const target = data.columns[0]
    const columns = data.columns.slice(1)
    const formatMap = data.formatMap
    const labelMap = data.labelMap
    const seriesStyle = data.seriesStyle
    const name = data.name ? data.name : ''
    const colors = data.colors

    if (rows && rows.length === 0) {
      return false
    }

    let colorList = colors || ITEM_COLOR
    const colorIndex = (chartIndex || 0) % colorList.length
    let color = colorList[colorIndex]
    let options = extend(true, {}, chartOption)
    const dataYList = {}
    const series = []
    const seriesData = []
    let xFormatFun = null
    let yFormatFun = {}

    // x轴y轴数据
    rows.map((item, index) => {
      columns.map((y) => {
        if (item[y] !== undefined) {
          dataYList[y] = item[y]
        }
      })
    })

    // 设置 series
    columns.map((y, index) => {
      // 如果多条线，另外配颜色
      if (index !== 0) {
        color = colorList[index]
      }
      const yName = (labelMap && labelMap[y]) ? labelMap[y] : y
      seriesData.push({
        value: dataYList[y],
        name: yName
      })

      // 设置 y轴 格式化
      if (formatMap && formatMap[y]) {
        yFormatFun[y] = ChartHelper.formatType(formatMap[y])
      }
    })

    let seriesOption = extend(true, {}, getDefaultSeriesOptions({color}))
    if (seriesStyle) seriesOption = extend(true, { ...seriesOption }, seriesStyle)
    let item = extend(true, {
      data: seriesData,
      name: name
    }, seriesOption)
    series.push(item)

    // 设置 x轴 格式化
    if (formatMap && formatMap[target]) {
      xFormatFun = ChartHelper.formatType(formatMap[target])
    }

    // tooltip格式化
    options.tooltip.formatter = function (params, ticket, callback) {
      let text = ''
      let title = params.seriesName
      let x = ''
      let y = ''

      x = xFormatFun ? xFormatFun(params.name) : params.name
      let aYFormatFun = null
      if (labelMap && item.seriesName) {
        aYFormatFun = yFormatFun[getKeyByValue(labelMap, item.seriesName)]
      }
      if (item.color) {
        y += ChartHelper.DOT_COLOR(item.color)
      }
      if (aYFormatFun) {
        y += `${x}: ${aYFormatFun(params.value)} (${params.percent})</br>`
      } else {
        y += `${x}: ${params.value} (${params.percent})</br>`
      }
      text = `${title} <br> ${y}`
      return text
    }

    if (options.xAxis) delete options.xAxis
    if (options.yAxis) delete options.yAxis

    options.series = series
    // if (legend && legend.length > 1) {
    //   options.legend.data = legend
    // }

    return options
  }
}

export default ChartHelper
